import { useRef, useState } from 'react';
import {
  AVATAR_FETCH_MESSAGES,
  AVATAR_PX,
  MAX_AVATAR_BYTES,
  type AvatarFetchResult,
} from '@goyone/shared';
import { api } from '../lib/api';
import { Icon } from './Icon';
import { Avatar } from './People';

/**
 * اختيار صورة السجل.
 *
 * ## التصغير في المتصفح لا في الخادم
 *
 * صورة الهاتف ثلاثة ميجابايت، والمعروض منها ثمانية وعشرون بكسلًا في
 * الجدول. ورفعُها كما هي ليُصغَّرها الخادم يعني انتظارًا على شبكةٍ مصرية
 * لأجل بايتات تُرمى فورًا — والمتصفح يملك `canvas` ويفعلها في جزءٍ من
 * ثانية.
 *
 * ولا مكتبة: `canvas` في كل متصفح منذ عقد.
 *
 * ## والقصّ من الوسط
 *
 * الصورة المستطيلة تُقصّ مربّعًا من وسطها لا تُضغط إلى مربّع: الضغط يجعل
 * الوجوه عريضة أو نحيلة، والقصّ يُبقيها كما هي.
 */
export function AvatarPicker({
  name,
  value,
  email,
  onChange,
}: {
  /** الاسم — منه تُولَّد الحروف حين لا صورة. */
  name: string;
  value: string | null | undefined;
  /** البريد، لزرّ السحب. */
  email?: string | null;
  onChange: (dataUrl: string | null) => void;
}) {
  const file = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pick = async (chosen: File) => {
    setError(null);
    setNote(null);

    if (!chosen.type.startsWith('image/')) {
      setError('اختر ملف صورة.');
      return;
    }

    setBusy(true);
    try {
      const shrunk = await shrink(chosen);
      if (shrunk.length > MAX_AVATAR_BYTES) {
        setError('تعذّر تصغير الصورة بما يكفي. جرّب صورة أخرى.');
        return;
      }
      onChange(shrunk);
    } catch {
      setError('تعذّرت قراءة الصورة.');
    } finally {
      setBusy(false);
      /* تفريغ الحقل: اختيار الملف نفسه مرةً ثانية لا يُطلق الحدث بدونه. */
      if (file.current) file.current.value = '';
    }
  };

  const fromEmail = async () => {
    setError(null);
    setNote(null);

    const clean = (email ?? '').trim();
    if (!clean) {
      setNote(AVATAR_FETCH_MESSAGES['no-email']);
      return;
    }

    setBusy(true);
    try {
      const res = await api<AvatarFetchResult>('/crm/leads/avatar-lookup', {
        method: 'POST',
        body: { email: clean },
      });
      setNote(AVATAR_FETCH_MESSAGES[res.outcome]);
      if (res.avatarUrl) onChange(res.avatarUrl);
    } catch (e) {
      setError(e instanceof Error ? e.message : AVATAR_FETCH_MESSAGES.failed);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="avatar-picker">
      <Avatar name={name || '؟'} src={value} size={64} />

      <div className="avatar-picker-actions">
        <input
          ref={file}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          hidden
          onChange={(e) => {
            const chosen = e.target.files?.[0];
            if (chosen) void pick(chosen);
          }}
        />

        <div className="row" style={{ gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => file.current?.click()}
            disabled={busy}
          >
            <Icon name="plus" size={14} />
            {value ? 'بدّل الصورة' : 'ارفع صورة'}
          </button>

          {/*
            السحب بضغطةٍ صريحة لا مع كل حفظ.
            الطلب يقول للخدمة الخارجية «أحدهم يسأل عن هذا البريد» — وهو
            ما يجب أن يقرّره من يُدخل البيانات لا أن يقع خلفه.
          */}
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            onClick={() => void fromEmail()}
            disabled={busy}
            title="يسأل خدمة Gravatar عن صورة مرتبطة بهذا البريد"
          >
            <Icon name="mail" size={14} />
            اسحب من البريد
          </button>

          {value && (
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              onClick={() => {
                onChange(null);
                setNote(null);
                setError(null);
              }}
              disabled={busy}
            >
              <Icon name="trash" size={14} />
              أزل
            </button>
          )}
        </div>

        {busy && <span className="tiny mute">لحظة…</span>}
        {note && <span className="tiny mute">{note}</span>}
        {error && <span className="tiny" style={{ color: 'var(--danger)' }}>{error}</span>}
        {!busy && !note && !error && (
          <span className="tiny mute">
            بلا صورة تظهر حروف الاسم. تُصغَّر إلى {AVATAR_PX}×{AVATAR_PX}.
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * يقرأ الملف ويُخرج مربّعًا صغيرًا بصيغة JPEG.
 *
 * JPEG لا PNG: الصور الشخصية فوتوغرافية، وPNG لها يُنتج ملفًا أضعافًا بلا
 * فائدة. والجودة `0.82` حدٌّ لا يُرى فرقه في ثمانيةٍ وعشرين بكسلًا.
 */
async function shrink(input: File): Promise<string> {
  const bitmap = await createImageBitmap(input);

  try {
    /* القصّ من الوسط: الضغط إلى مربّع يجعل الوجوه عريضة أو نحيلة. */
    const side = Math.min(bitmap.width, bitmap.height);
    const sx = (bitmap.width - side) / 2;
    const sy = (bitmap.height - side) / 2;

    const canvas = document.createElement('canvas');
    canvas.width = AVATAR_PX;
    canvas.height = AVATAR_PX;

    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no 2d context');

    ctx.drawImage(bitmap, sx, sy, side, side, 0, 0, AVATAR_PX, AVATAR_PX);
    return canvas.toDataURL('image/jpeg', 0.82);
  } finally {
    /* الصورة النقطية مورد: تركُها يحجز ذاكرةً حتى يجمعها الكانس. */
    bitmap.close();
  }
}
